import { z } from 'zod';

import { MERCHANT_REPORT_STATUSES } from '../consts';
import { EcosystemRecordSchema } from './report-schema';

/**
 * Company Analysis Report Status
 * This is used for both raw and transformed report shapes
 */
const CompanyAnalysisReportStatusEnum = z.enum([
  MERCHANT_REPORT_STATUSES[0],
  ...MERCHANT_REPORT_STATUSES.slice(1),
]);

export type CompanyAnalysisReportStatus = z.infer<typeof CompanyAnalysisReportStatusEnum>;

/**
 * Risk level for company analysis
 */
export const COMPANY_ANALYSIS_RISK_LEVELS = ['low', 'medium', 'high', 'critical'] as const;
export type CompanyAnalysisSynthesizerRiskLevel = (typeof COMPANY_ANALYSIS_RISK_LEVELS)[number];

/**
 * Risk types for company analysis
 */
export const COMPANY_ANALYSIS_RISK_TYPE_KEYS = ['chargebacks', 'reputational', 'legal'] as const;
export type CompanyAnalysisRiskTypeKey = (typeof COMPANY_ANALYSIS_RISK_TYPE_KEYS)[number];

// ============================================================================
// Helper schemas used by transformed types
// ============================================================================

const CompanyAnalysisRiskTypeSchema = z.object({
  status: z.enum(['clear', 'alert']),
});

const CompanyAnalysisTagSchema = z.object({
  name: z.string(),
  level: z.enum(['positive', 'moderate', 'negative']),
});

const CompanyAnalysisMccSuggestionSchema = z.object({
  mcc: z.string(),
  description: z.string().nullish(),
  reasoning: z.string().nullish(),
});

const CompanyAnalysisLineOfBusinessSchema = z.object({
  lobFromWebsite: z.string().nullish(),
  mcc: z
    .object({
      primary: CompanyAnalysisMccSuggestionSchema.nullish(),
      alternatives: z.array(CompanyAnalysisMccSuggestionSchema).max(2).default([]),
    })
    .nullish(),
});

const CompanyAnalysisSynthesizerMerchantDetailsSchema = z.object({
  companyName: z.string().nullish(),
  registrationNumber: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  email: z.string().nullish(),
  country: z.string().nullish(),
  address: z.string().nullish(),
  website: z.string().nullish(),
});

const CompanyAnalysisSocialPageDataSchema = z.union([
  z.object({
    url: z.string().min(1),
    status: z.literal('detected'),
  }),
  z.object({
    url: z.union([z.null(), z.literal('')]),
    status: z.literal('missing'),
  }),
]);

const CompanyAnalysisSocialInfoSchema = z.object({
  facebook: CompanyAnalysisSocialPageDataSchema,
  instagram: CompanyAnalysisSocialPageDataSchema,
  tiktok: CompanyAnalysisSocialPageDataSchema,
  linkedin: CompanyAnalysisSocialPageDataSchema,
  twitter: CompanyAnalysisSocialPageDataSchema,
});

const CompanyAnalysisSocialDataSchema = z.object({
  url: z.string().nullable(),
  status: z.union([z.literal('detected'), z.literal('missing')]),
});

export const CompanyAnalysisSocialMediaResponseSchema = z.record(
  z.enum(['facebook', 'instagram', 'tiktok', 'linkedin', 'twitter']),
  CompanyAnalysisSocialDataSchema,
);

export type CompanyAnalysisSocialMediaResponse = z.infer<
  typeof CompanyAnalysisSocialMediaResponseSchema
>;

// ============================================================================
// Transformed/Normalized types for UI consumption
// These are the shapes returned by the API after backend transformation
// ============================================================================
export const NormalizedMerchantDetailsSchema = z
  .object({
    companyName: z.string().nullable(),
    registrationNumber: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    email: z.string().nullable(),
    country: z.string().nullable(),
    address: z.string().nullable(),
    website: z.string().nullable(),
  })
  .nullable();

export type NormalizedMerchantDetails = z.infer<typeof NormalizedMerchantDetailsSchema>;

export const NormalizedProvidedDetailsSchema = z
  .object({
    companyName: z.string().nullable(),
    registrationNumber: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    country: z.string().nullable(),
    address: z.string().nullable(),
    website: z.string().nullable(),
    lineOfBusiness: z.string().nullable(),
    mcc: z.string().nullable(),
  })
  .nullable();

export type NormalizedProvidedDetails = z.infer<typeof NormalizedProvidedDetailsSchema>;

export const NormalizedMccSchema = z.object({
  code: z.string(),
  category: z.string().nullable(),
  reasoning: z.string().nullable(),
});

export type NormalizedMcc = z.infer<typeof NormalizedMccSchema>;

export const NormalizedOperationalProofSchema = z
  .object({
    lineOfBusiness: z.string().nullable(),
    lineOfBusinessReasoning: z.string().nullable(),
    mcc: NormalizedMccSchema.nullable(),
    alternatives: z.array(NormalizedMccSchema),
  })
  .nullable();

export type NormalizedOperationalProof = z.infer<typeof NormalizedOperationalProofSchema>;

export const NormalizedSynthesizerResultSchema = z
  .object({
    tags: z.array(CompanyAnalysisTagSchema),
    riskLevel: z.enum(['low', 'medium', 'high', 'critical']),
    riskTypes: z.object({
      chargebacks: CompanyAnalysisRiskTypeSchema,
      reputational: CompanyAnalysisRiskTypeSchema,
      legal: CompanyAnalysisRiskTypeSchema,
    }),
    riskAnalysis: z.string(),
    references: z.string(),
    data: z
      .object({
        lineOfBusiness: CompanyAnalysisLineOfBusinessSchema.nullish(),
        merchantDetails: CompanyAnalysisSynthesizerMerchantDetailsSchema.nullish(),
        socialInfo: CompanyAnalysisSocialInfoSchema.nullish(),
      })
      .nullish(),
  })
  .nullable();

export type NormalizedSynthesizerResult = z.infer<typeof NormalizedSynthesizerResultSchema>;

export const StructuredTagWithLevelSchema = z.object({
  name: z.string(),
  level: z.enum(['positive', 'moderate', 'negative']),
  correlatesTo: z.string().nullable(),
});

export type StructuredTagWithLevel = z.infer<typeof StructuredTagWithLevelSchema>;

export const TransformedCompanyAnalysisReportDataSchema = z.object({
  matchReason: z.string().nullable(),
  merchantDetails: NormalizedMerchantDetailsSchema,
  providedDetails: NormalizedProvidedDetailsSchema,
  operationalProof: NormalizedOperationalProofSchema,
  ecosystem: z.array(EcosystemRecordSchema).nullable(),
  socialMedia: CompanyAnalysisSocialMediaResponseSchema.nullable(),
  synthesizerResult: NormalizedSynthesizerResultSchema,
  structuredTags: z.array(StructuredTagWithLevelSchema),
});

export type TransformedCompanyAnalysisReportData = z.infer<
  typeof TransformedCompanyAnalysisReportDataSchema
>;

export const TransformedCompanyAnalysisReportSchema = z.object({
  id: z.string(),
  status: CompanyAnalysisReportStatusEnum,
  createdAt: z.string(),
  updatedAt: z.string(),
  data: TransformedCompanyAnalysisReportDataSchema,
  workflowStep: z.string().nullable(),
});

export type TransformedCompanyAnalysisReport = z.infer<
  typeof TransformedCompanyAnalysisReportSchema
>;

export const TransformedCompanyAnalysisReportsResponseSchema = z.array(
  TransformedCompanyAnalysisReportSchema,
);
